import React, { useEffect, useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, MapPin, ChevronRight, ChevronLeft } from 'lucide-react';

const MetricBox = ({ label, value }) => (
  <div className="border border-[#4A4A4A] bg-[#0D0D0D] p-3 sm:p-4">
    <p className="text-[#6B7280] text-[9px] sm:text-[10px] uppercase tracking-widest font-body mb-1">{label}</p>
    <p className="font-heading text-sm sm:text-base text-white leading-tight">{value}</p>
  </div>
);

const ProjectViewsSection = ({ images, projectName }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  
  const MAX_VISIBLE = 4;
  const showMore = images && images.length > MAX_VISIBLE;
  const visibleImages = showMore ? images.slice(0, MAX_VISIBLE) : (images || []);
  const extraCount = showMore ? images.length - MAX_VISIBLE : 0;

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  
  const nextImage = useCallback((e) => { 
    e.stopPropagation(); 
    setLightboxIndex((prev) => (prev + 1) % images.length); 
  }, [images]);

  const prevImage = useCallback((e) => { 
    e.stopPropagation(); 
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length); 
  }, [images]);

  // Add keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextImage(e);
      if (e.key === 'ArrowLeft') prevImage(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, nextImage, prevImage]);

  return (
    <div className="mt-10" data-testid="modal-gallery">
      <p className="text-[#C41E3A] text-xs uppercase tracking-[0.2em] font-body mb-1">Project Gallery</p>
      <h4 className="font-heading text-2xl text-white mb-6">PROJECT VIEWS</h4>
      {images && images.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#2A2A2A]">
          {visibleImages.map((src, i) => {
            const isLastVisible = i === MAX_VISIBLE - 1 && showMore;
            return (
              <div 
                key={i} 
                className="aspect-[4/3] overflow-hidden bg-[#1A1A1A] relative cursor-pointer group"
                onClick={() => openLightbox(i)}
              >
                <img
                  src={src}
                  alt={`${projectName} view ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 grayscale-[15%] group-hover:grayscale-0"
                />
                {isLastVisible && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                    <span className="text-white font-heading text-2xl sm:text-3xl">+{extraCount}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border border-[#2A2A2A] bg-[#0D0D0D] p-8 text-center">
          <p className="text-[#6B7280] text-sm font-body">Project photos coming soon.</p>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxIndex !== null && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10001] bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button 
            className="absolute top-5 right-5 text-[#9CA3AF] hover:text-[#C41E3A] transition-colors p-2 z-[10002]"
            onClick={closeLightbox}
            aria-label="Close Gallery"
          >
            <X size={32} />
          </button>

          {images.length > 1 && (
            <button 
              className="absolute left-2 sm:left-8 text-white hover:text-[#C41E3A] transition-colors bg-black/50 hover:bg-black/80 p-2 sm:p-3 rounded-full z-[10002]"
              onClick={prevImage}
              aria-label="Previous Image"
            >
              <ChevronLeft size={28} />
            </button>
          )}

          <img 
            src={images[lightboxIndex]} 
            alt={`${projectName} view ${lightboxIndex + 1}`}
            className="max-w-[95vw] sm:max-w-[85vw] max-h-[85vh] object-contain relative z-[10001]"
            onClick={(e) => e.stopPropagation()}
          />

          {images.length > 1 && (
            <button 
              className="absolute right-2 sm:right-8 text-white hover:text-[#C41E3A] transition-colors bg-black/50 hover:bg-black/80 p-2 sm:p-3 rounded-full z-[10002]"
              onClick={nextImage}
              aria-label="Next Image"
            >
              <ChevronRight size={28} />
            </button>
          )}

          <div className="absolute bottom-6 sm:bottom-10 text-[#9CA3AF] font-body text-sm bg-black/50 px-4 py-1.5 rounded-full">
            {lightboxIndex + 1} / {images.length}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export const ProjectModal = ({ project, onClose }) => {
  const handleKey = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  if (!project) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black overflow-y-auto"
      style={{ zIndex: 9999 }}
      role="dialog"
      aria-modal="true"
      aria-label={project.name}
      data-testid="project-modal"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        data-testid="modal-close-btn"
        className="fixed top-5 right-5 w-10 h-10 border border-[#4A4A4A] bg-[#0A0A0A] flex items-center justify-center hover:border-[#C41E3A] hover:text-[#C41E3A] text-[#9CA3AF] transition-colors"
        style={{ zIndex: 10000 }}
        aria-label="Close"
      >
        <X size={16} strokeWidth={1.5} />
      </button>

      {/* Hero Image */}
      <div className="relative h-[40vh] lg:h-[50vh] shrink-0 overflow-hidden">
        <img
          src={project.heroImg}
          alt={project.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 lg:px-8 pb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-[#C41E3A] text-white text-[10px] font-heading tracking-widest px-2.5 py-1">{project.badge}</span>
            <span className="text-[#9CA3AF] text-[10px] font-body uppercase tracking-widest">{project.category}</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-5xl text-white leading-none">{project.name}</h2>
          <div className="flex items-center gap-1.5 mt-2">
            <MapPin size={12} strokeWidth={1.5} className="text-[#C41E3A]" />
            <span className="text-[#9CA3AF] text-sm font-body">{project.location}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 pb-20">
        {/* Key Metrics — always 3 columns (no empty box) */}
        <div className="grid grid-cols-3 gap-px bg-[#4A4A4A] mb-8" data-testid="modal-metrics">
          <MetricBox label="Built-up Area" value={project.area} />
          <MetricBox label="Location / Zone" value={project.location} />
          <MetricBox label="Category" value={project.category} />
        </div>

        {/* Description */}
        <div className="mb-8">
          <p className="text-[#C41E3A] text-xs uppercase tracking-[0.2em] font-body mb-3">Project Overview</p>
          <p className="text-[#D1D5DB] text-base font-body leading-relaxed">{project.description}</p>
        </div>

        {/* Scope of Work */}
        <div className="border-t border-[#2A2A2A] pt-8 mb-2" data-testid="modal-scope">
          <p className="text-[#C41E3A] text-xs uppercase tracking-[0.2em] font-body mb-1">Scope of Work</p>
          <h4 className="font-heading text-2xl text-white mb-5">WHAT WAS DELIVERED</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {project.scopeOfWork.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-[#0D0D0D] border border-[#2A2A2A] px-4 py-3">
                <ChevronRight size={12} strokeWidth={2} className="text-[#C41E3A] mt-0.5 shrink-0" />
                <p className="text-[#D1D5DB] text-sm font-body leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Project Views — always shown */}
        <ProjectViewsSection images={project.gallery} projectName={project.name} />

        {/* CTA */}
        <div className="mt-12 border border-[#4A4A4A] bg-[#0D0D0D] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-heading text-xl text-white">NEED A SIMILAR FACILITY?</p>
            <p className="text-[#9CA3AF] text-sm font-body mt-1">Let's discuss your industrial construction requirements.</p>
          </div>
          <a
            href="/#contact"
            onClick={onClose}
            data-testid="modal-cta-btn"
            className="shrink-0 bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-7 py-4 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors whitespace-nowrap"
          >
            Get In Touch
          </a>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
